import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlay, FaPause, FaRedo, FaTimes, FaTrophy, FaAppleAlt } from 'react-icons/fa';

const GRID_SIZE = 20;
const INITIAL_SNAKE = [{ x: 10, y: 10 }];
const INITIAL_DIRECTION = { x: 0, y: -1 };
const INITIAL_FOOD = { x: 15, y: 15 };
const GAME_SPEED = 150;

const RetroSnakeGame = ({ isOpen, onClose }) => {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [food, setFood] = useState(INITIAL_FOOD);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('retro-snake-high-score') || '0');
  });
  const [gameOver, setGameOver] = useState(false);
  const gameLoopRef = useRef();
  const directionRef = useRef(INITIAL_DIRECTION);

  // Generate random food position
  const generateFood = useCallback((currentSnake) => {
    let newFood;
    do {
      newFood = {
        x: Math.floor(Math.random() * GRID_SIZE),
        y: Math.floor(Math.random() * GRID_SIZE)
      };
    } while (currentSnake.some(segment => segment.x === newFood.x && segment.y === newFood.y));
    return newFood;
  }, []);

  // Reset game to initial state
  const resetGame = useCallback(() => {
    setSnake(INITIAL_SNAKE);
    setDirection(INITIAL_DIRECTION);
    directionRef.current = INITIAL_DIRECTION;
    setFood(generateFood(INITIAL_SNAKE));
    setScore(0);
    setGameOver(false);
    setIsPlaying(false);
    setIsPaused(false);
  }, [generateFood]);

  // Game loop
  const gameLoop = useCallback(() => {
    setSnake(currentSnake => {
      if (gameOver || isPaused) return currentSnake;

      const newSnake = [...currentSnake];
      const head = { ...newSnake[0] };
      
      // Move head in current direction
      head.x += directionRef.current.x;
      head.y += directionRef.current.y;

      // Check wall collisions
      if (head.x < 0 || head.x >= GRID_SIZE || head.y < 0 || head.y >= GRID_SIZE) {
        setGameOver(true);
        setIsPlaying(false);
        return currentSnake;
      }

      // Check self collision
      if (newSnake.some(segment => segment.x === head.x && segment.y === head.y)) {
        setGameOver(true);
        setIsPlaying(false);
        return currentSnake;
      }

      newSnake.unshift(head);

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        setScore(prev => {
          const newScore = prev + 10;
          if (newScore > highScore) {
            setHighScore(newScore);
            localStorage.setItem('retro-snake-high-score', newScore.toString());
          }
          return newScore;
        });
        setFood(generateFood(newSnake));
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  }, [food, gameOver, isPaused, highScore, generateFood]);

  // Handle click/touch input for movement
  const handleCellClick = useCallback((clickedX, clickedY) => {
    if (!isPlaying || isPaused || gameOver || snake.length === 0) return;

    const headX = snake[0].x;
    const headY = snake[0].y;
    
    // Calculate the difference between clicked cell and snake head
    const deltaX = clickedX - headX;
    const deltaY = clickedY - headY;
    
    let newDirection = { ...directionRef.current };
    
    // Determine direction based on which axis has the larger absolute difference
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal movement
      if (directionRef.current.x === 0) { // Only allow if not already moving horizontally
        newDirection.x = deltaX > 0 ? 1 : -1;
        newDirection.y = 0;
      }
    } else if (Math.abs(deltaY) > 0) {
      // Vertical movement
      if (directionRef.current.y === 0) { // Only allow if not already moving vertically
        newDirection.x = 0;
        newDirection.y = deltaY > 0 ? 1 : -1;
      }
    }
    
    // Only update if direction actually changed
    if (newDirection.x !== directionRef.current.x || newDirection.y !== directionRef.current.y) {
      setDirection(newDirection);
      directionRef.current = newDirection;
    }
  }, [isPlaying, isPaused, gameOver, snake]);

  // Handle spacebar for pause (keep this for convenience)
  const handleKeyPress = useCallback((e) => {
    if (e.key === ' ') {
      e.preventDefault();
      if (isPlaying && !gameOver) {
        setIsPaused(prev => !prev);
      }
    }
  }, [isPlaying, gameOver]);

  // Start game
  const startGame = () => {
    resetGame();
    setIsPlaying(true);
    setIsPaused(false);
  };

  // Toggle pause
  const togglePause = () => {
    if (isPlaying && !gameOver) {
      setIsPaused(prev => !prev);
    }
  };

  // Set up game loop
  useEffect(() => {
    if (isPlaying && !isPaused && !gameOver) {
      gameLoopRef.current = setInterval(gameLoop, GAME_SPEED);
    } else {
      clearInterval(gameLoopRef.current);
    }

    return () => clearInterval(gameLoopRef.current);
  }, [isPlaying, isPaused, gameOver, gameLoop]);

  // Set up keyboard listeners
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [isOpen, handleKeyPress]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clearInterval(gameLoopRef.current);
    };
  }, []);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 max-w-2xl w-full shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center">
              🐍
            </div>
                         <div>
               <h2 className="text-2xl font-['VT323'] text-black/70">Retro Snake</h2>
               <p className="text-sm font-['VT323'] text-black/50">Click around the snake's head to move</p>
             </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
          >
            <FaTimes className="w-5 h-5 text-black/40" />
          </button>
        </div>

        {/* Score Display */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-['VT323'] text-black/70">Score: {score}</div>
              <div className="text-sm font-['VT323'] text-black/50">Length: {snake.length}</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-['VT323'] text-yellow-600 flex items-center gap-1">
                <FaTrophy className="w-4 h-4" />
                High: {highScore}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {!isPlaying ? (
              <motion.button
                onClick={startGame}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-600 hover:bg-green-500/30 rounded-lg font-['VT323'] text-[18px] transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FaPlay className="w-4 h-4" />
                {gameOver ? 'Play Again' : 'Start'}
              </motion.button>
            ) : (
              <motion.button
                onClick={togglePause}
                className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 text-blue-600 hover:bg-blue-500/30 rounded-lg font-['VT323'] text-[18px] transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {isPaused ? <FaPlay className="w-4 h-4" /> : <FaPause className="w-4 h-4" />}
                {isPaused ? 'Resume' : 'Pause'}
              </motion.button>
            )}
            
            <motion.button
              onClick={resetGame}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-600 hover:bg-orange-500/30 rounded-lg font-['VT323'] text-[18px] transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FaRedo className="w-4 h-4" />
              Reset
            </motion.button>
          </div>
        </div>

        {/* Game Board */}
        <div className="relative bg-black/90 rounded-xl p-4 mb-4">
          <div 
            className="relative grid gap-0 mx-auto border-2 border-green-400/30"
            style={{ 
              gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
              width: '400px',
              height: '400px'
            }}
          >
            {/* Grid cells */}
            {Array.from({ length: GRID_SIZE * GRID_SIZE }).map((_, index) => {
              const x = index % GRID_SIZE;
              const y = Math.floor(index / GRID_SIZE);
              const isSnakeSegment = snake.some(segment => segment.x === x && segment.y === y);
              const isSnakeHead = snake[0]?.x === x && snake[0]?.y === y;
              const isFood = food.x === x && food.y === y;
              
                             return (
                 <div
                   key={index}
                   onClick={() => handleCellClick(x, y)}
                   className={`border border-green-400/10 transition-all duration-100 cursor-pointer ${
                     isSnakeHead
                       ? 'bg-green-400 border-green-300 shadow-lg shadow-green-400/50'
                       : isSnakeSegment
                       ? 'bg-green-500/80'
                       : isFood
                       ? 'bg-red-500 border-red-400 shadow-lg shadow-red-500/50'
                       : 'bg-transparent hover:bg-green-400/5'
                   }`}
                 >
                  {isFood && (
                    <div className="w-full h-full flex items-center justify-center text-xs">
                      🍎
                    </div>
                  )}
                  {isSnakeHead && (
                    <div className="w-full h-full flex items-center justify-center text-xs">
                      👁
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Game Over Overlay */}
          <AnimatePresence>
            {gameOver && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-xl flex items-center justify-center"
              >
                <div className="text-center text-white">
                  <div className="text-4xl font-['VT323'] mb-2">Game Over!</div>
                  <div className="text-xl font-['VT323'] text-white/80 mb-4">Final Score: {score}</div>
                  {score === highScore && score > 0 && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="text-yellow-400 font-['VT323'] text-lg mb-4 flex items-center justify-center gap-2"
                    >
                      <FaTrophy className="w-5 h-5" />
                      New High Score!
                    </motion.div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pause Overlay */}
          <AnimatePresence>
            {isPaused && isPlaying && !gameOver && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/60 backdrop-blur-sm rounded-xl flex items-center justify-center"
              >
                <div className="text-center text-white">
                  <div className="text-3xl font-['VT323']">Paused</div>
                  <div className="text-sm font-['VT323'] text-white/80 mt-2">Press Space or click Resume to continue</div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

                 {/* Instructions */}
         <div className="text-center text-sm font-['VT323'] text-black/50 space-y-1">
           <div>🎮 Click left/right/above/below the 👁 to move • Spacebar to pause</div>
           <div>🍎 Eat the apples to grow • Don't hit the walls or yourself!</div>
         </div>
      </motion.div>
    </motion.div>
  );
};

export default RetroSnakeGame; 